<template>
    <div class="mt-2 shadow-xl">
        <div v-if="fileId3List.length === 0" class="metadata-empty" role="status">
            <workspace-empty-state
                :title="$tt('暂无信息')"
                :description="$tt('选择要处理的文件后，批量元数据会显示在这里。')"
            />
        </div>
        <div v-else>
            <!-- 功能栏 -->
            <div class="flex justify-between">
                <div class="flex gap-2 items-end ml-4">
                    <div @click="tableSize = 'xs'" class="cursor-pointer">
                        <svg-icon name="small" class="w-4 h-4" :class="{'fill-primary': tableSize === 'xs'}"></svg-icon>
                    </div>
                    <div @click="tableSize = 'sm'" class="cursor-pointer">
                        <svg-icon name="small" class="w-5 h-5" :class="{'fill-primary': tableSize === 'sm'}"></svg-icon>
                    </div>
                    <div @click="tableSize = 'md'" class="cursor-pointer">
                        <svg-icon name="small" class="w-6 h-6" :class="{'fill-primary': tableSize === 'md'}"></svg-icon>
                    </div>
                    <!-- 每页显示数量选择 -->
                    <div class="ml-4 flex items-center gap-2">
                        <label class="text-sm text-base-content/60">{{ $tt('每页:') }}</label>
                        <select v-model="pageSize" @change="handlePageSizeChange" class="select select-sm select-ghost">
                            <option :value="10">10</option>
                            <option :value="32">32</option>
                            <option :value="128">128</option>
                            <option :value="10240">10240</option>
                        </select>
                    </div>
                </div>
                <div class="flex justify-end">
                    <div class="text-base-content/50 mr-2 flex items-center">
                        <div class="flex" v-if="folderNumber > 0 || filesNumber > 0">
                            <div class="mr-2"></div>
                            <div v-if="filesNumber > 0" class="flex items-center mr-2">
                                {{ filesNumber }}
                                <svg t="1733058796790" class="h-4 w-4 fill-current ml-1" viewBox="0 0 1024 1024"
                                    version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="14983" width="200"
                                    height="200">
                                    <path
                                        d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326z m1.8 562H232V136h302v216c0 23.2 18.8 42 42 42h216v494z"
                                        p-id="14984"></path>
                                </svg>
                            </div>
                            <div v-if="folderNumber > 0" class="flex items-center mr-2">
                                {{ folderNumber }}
                                <svg t="1733058876424" class="h-4 w-4 fill-current ml-1" viewBox="0 0 1024 1024"
                                    version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="15135" width="200"
                                    height="200">
                                    <path
                                        d="M921.6 450.133333c-6.4-8.533333-14.933333-12.8-25.6-12.8h-10.666667V341.333333c0-40.533333-34.133333-74.666667-74.666666-74.666666H514.133333c-4.266667 0-6.4-2.133333-8.533333-4.266667l-38.4-66.133333c-12.8-21.333333-38.4-36.266667-64-36.266667H170.666667c-40.533333 0-74.666667 34.133333-74.666667 74.666667v597.333333c0 6.4 2.133333 12.8 6.4 19.2 6.4 8.533333 14.933333 12.8 25.6 12.8h640c12.8 0 25.6-8.533333 29.866667-21.333333l128-362.666667c4.266667-10.666667 2.133333-21.333333-4.266667-29.866667zM170.666667 224h232.533333c4.266667 0 6.4 2.133333 8.533333 4.266667l38.4 66.133333c12.8 21.333333 38.4 36.266667 64 36.266667H810.666667c6.4 0 10.666667 4.266667 10.666666 10.666666v96H256c-12.8 0-25.6 8.533333-29.866667 21.333334l-66.133333 185.6V234.666667c0-6.4 4.266667-10.666667 10.666667-10.666667z m573.866666 576H172.8l104.533333-298.666667h571.733334l-104.533334 298.666667z">
                                    </path>
                                </svg>
                            </div>
                        </div>
                        <div class="mr-1">{{ $tt('数量：{count}', { count: fileId3List.length }) }}</div>
                    </div>
                </div>
            </div>

            <div class="overflow-x-auto h-[calc(100vh-130px)]" @scroll="handleScroll" @mouseup="handleMouseUp" ref="scrollContainer">
                <table :class="['table', `table-${tableSize}`, 'table-pin-rows']">
                    <!-- head -->
                    <thead>
                        <tr>
                            <th v-for="(item, index) in showFieldsID3" :key="'id3' + index" 
                                :class="item === 'checkbox' ? 'w-12' : ''">
                                <!-- 全选复选框列 -->
                                <div v-if="item === 'checkbox'" class="flex justify-center">
                                    <input type="checkbox" 
                                           :checked="isAllSelected" 
                                           @change="toggleAllSelection"
                                           class="checkbox checkbox-sm" />
                                </div>
                                <div @click="sortBy('artwork')" class="cursor-pointer" v-if="item === 'album_img'">
                                    <div class="flex justify-center">
                                        <div>{{ $tt('封面') }}</div>
                                        <div v-if="nowSortedKey === 'artwork'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('title')" class="cursor-pointer" v-else-if="item === 'title'">
                                    <div class="flex">
                                        <div>{{ $tt('标题') }}</div>
                                        <div v-if="nowSortedKey === 'title'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('filename')" class="cursor-pointer" v-else-if="item === 'filename'">
                                    <div class="flex">
                                        <div>{{ $tt('文件名') }}</div>
                                        <div v-if="nowSortedKey === 'filename'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('artist')" class="cursor-pointer" v-else-if="item === 'artist'">
                                    <div class="flex">
                                        <div>{{ $tt('艺术家') }}</div>
                                        <div v-if="nowSortedKey === 'artist'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('album')" class="cursor-pointer w-16" v-else-if="item === 'album'">
                                    <div class="flex">
                                        <div>{{ $tt('专辑') }}</div>
                                        <div v-if="nowSortedKey === 'album'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('lyrics')" class="cursor-pointer" v-else-if="item === 'lyrics'">
                                    <div class="flex">
                                        <div>{{ $tt('歌词') }}</div>
                                        <div v-if="nowSortedKey === 'lyrics'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('lyrics_file')" class="cursor-pointer"
                                    v-else-if="item === 'lyrics_file'">
                                    <div class="flex">
                                        <div>LRC</div>
                                        <div v-if="nowSortedKey === 'lyrics_file'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('tracknumber')" class="cursor-pointer"
                                    v-else-if="item === 'tracknumber'">
                                    <div class="flex">
                                        <div>{{ $tt('音轨号') }}</div>
                                        <div v-if="nowSortedKey === 'tracknumber'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('discnumber')" class="cursor-pointer"
                                    v-else-if="item === 'discnumber'">
                                    <div class="flex">
                                        <div>{{ $tt('CD号') }}</div>
                                        <div v-if="nowSortedKey === 'discnumber'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('year')" class="cursor-pointer" v-else-if="item === 'year'">
                                    <div class="flex">
                                        <div>{{ $tt('年份') }}</div>
                                        <div v-if="nowSortedKey === 'year'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('albumartist')" class="cursor-pointer"
                                    v-else-if="item === 'albumartist'">
                                    <div class="flex">
                                        <div>{{ $tt('专辑艺术家') }}</div>
                                        <div v-if="nowSortedKey === 'albumartist'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('album_type')" class="cursor-pointer"
                                    v-else-if="item === 'album_type'">
                                    <div class="flex">
                                        <div>{{ $tt('专辑类型') }}</div>
                                        <div v-if="nowSortedKey === 'album_type'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('genre')" class="cursor-pointer" v-else-if="item === 'genre'">
                                    <div class="flex">
                                        <div>{{ $tt('风格') }}</div>
                                        <div v-if="nowSortedKey === 'genre'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('comment')" class="cursor-pointer" v-else-if="item === 'comment'">
                                    <div class="flex">
                                        <div>{{ $tt('简介') }}</div>
                                        <div v-if="nowSortedKey === 'comment'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('description')" class="cursor-pointer"
                                    v-else-if="item === 'description'">
                                    <div class="flex">
                                        <div>{{ $tt('描述') }}</div>
                                        <div v-if="nowSortedKey === 'description'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('totaltracks')" class="cursor-pointer"
                                    v-else-if="item === 'totaltracks'">
                                    <div class="flex">
                                        <div>{{ $tt('总音轨数') }}</div>
                                        <div v-if="nowSortedKey === 'totaltracks'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('totaldiscs')" class="cursor-pointer"
                                    v-else-if="item === 'totaldiscs'">
                                    <div class="flex">
                                        <div>{{ $tt('总CD数') }}</div>
                                        <div v-if="nowSortedKey === 'totaldiscs'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('duration')" class="cursor-pointer" v-else-if="item === 'duration'">
                                    <div class="flex">
                                        <div>{{ $tt('时长') }}</div>
                                        <div v-if="nowSortedKey === 'duration'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('size')" class="cursor-pointer" v-else-if="item === 'size'">
                                    <div class="flex">
                                        <div>{{ $tt('文件大小') }}</div>
                                        <div v-if="nowSortedKey === 'size'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('bit_rate')" class="cursor-pointer" v-else-if="item === 'bit_rate'">
                                    <div class="flex">
                                        <div>{{ $tt('比特率') }}</div>
                                        <div v-if="nowSortedKey === 'bit_rate'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('sample_rate')" class="cursor-pointer"
                                    v-else-if="item === 'sample_rate'">
                                    <div class="flex">
                                        <div>{{ $tt('采样率') }}</div>
                                        <div v-if="nowSortedKey === 'sample_rate'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('channels')" class="cursor-pointer" v-else-if="item === 'channels'">
                                    <div class="flex">
                                        <div>{{ $tt('声道') }}</div>
                                        <div v-if="nowSortedKey === 'channels'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('bit_depth')" class="cursor-pointer"
                                    v-else-if="item === 'bit_depth'">
                                    <div class="flex">
                                        <div>{{ $tt('位深') }}</div>
                                        <div v-if="nowSortedKey === 'bit_depth'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('language')" class="cursor-pointer" v-else-if="item === 'language'">
                                    <div class="flex">
                                        <div>{{ $tt('语言') }}</div>
                                        <div v-if="nowSortedKey === 'language'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('composer')" class="cursor-pointer" v-else-if="item === 'composer'">
                                    <div class="flex">
                                        <div>{{ $tt('作曲家') }}</div>
                                        <div v-if="nowSortedKey === 'composer'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                                <div @click="sortBy('lyricist')" class="cursor-pointer" v-else-if="item === 'lyricist'">
                                    <div class="flex">
                                        <div>{{ $tt('作词家') }}</div>
                                        <div v-if="nowSortedKey === 'lyricist'">
                                            <svg-icon :name="nowSortedOrder"
                                                class="fill-neutral-content w-4 h-4"></svg-icon>
                                        </div>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-base-200">
                        <tr v-for="(item, index) in fileId3List" :key="index" 
                            :class="{'bg-primary/10': selectedItemIds.includes(getItemId(item, index))}"
                            @mouseenter="handleMouseEnter(item, index, $event)">
                            <td v-for="(item2, index2) in showFieldsID3" :key="index + '-' + index2"
                                :class="item2 === 'checkbox' ? 'w-12' : ''">
                                <!-- 复选框列 -->
                                <div v-if="item2 === 'checkbox'" class="flex justify-center">
                                    <div class="checkbox-wrapper"
                                         @mousedown.stop="handleMouseDown(item, index, $event)">
                                        <input type="checkbox" 
                                               :checked="selectedItemIds.includes(getItemId(item, index))"
                                               @change.stop="handleCheck(item, index)"
                                               class="checkbox checkbox-sm" />
                                    </div>
                                </div>
                                <div class="flex items-center gap-3" v-if="item2 === 'album_img'">
                                    <div>
                                        <div :class="[
                                            'grid place-items-center',
                                            tableSize === 'xs' ? 'h-10 w-10' :
                                            tableSize === 'sm' ? 'h-14 w-14' :
                                            'h-20 w-20'
                                        ]">
                                            <div>
                                                <div class="cursor-pointer" @click="openImagePreview(item)">
                                                    <img :src="item.artwork" alt="" v-if="item.artwork" class="rounded-xl hover:opacity-80 transition-opacity"/>
                                                    <div v-else>
                                                        <div class="skeleton" :class="[
                                                            tableSize === 'xs' ? 'h-10 w-10' :
                                                            tableSize === 'sm' ? 'h-14 w-14' :
                                                            'h-20 w-20'
                                                        ]"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'filename'" class="flex items-center gap-2">
                                    <div @click="handleEdit2(item)"
                                        class="cursor-pointer hover:underline truncate text-ellipsis max-w-24 md:max-w-48">
                                        {{ item.filename }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'title'" class="truncate text-ellipsis max-w-24 md:max-w-48">
                                    {{ item.title }}
                                </div>
                                <div v-else-if="item2 === 'artist'" :class="tableSize === 'xs' ? 'truncate max-w-24' : ''">{{ item.artist }}</div>
                                <div v-else-if="item2 === 'album'" :class="tableSize === 'xs' ? 'truncate max-w-24' : ''">{{ item.album }}</div>
                                <div v-else-if="item2 === 'lyrics'">
                                    <div>
                                        <div class="badge badge-outline" v-if="item.lyrics">{{ $tt('有') }}</div>
                                        <div class="badge badge-primary badge-outline cursor-pointer" v-else
                                            @click="toggleLock(item)">{{ $tt('无') }}
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'lyrics_file'">
                                    <div>
                                        <div class="badge badge-outline" v-if="item.lyrics_file">{{ $tt('有') }}</div>
                                        <div class="badge badge-primary badge-outline" v-else>{{ $tt('无') }}</div>
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'tracknumber'">
                                    <div v-if="item.tracknumber" class="flex">
                                        <div>
                                            {{ item.tracknumber }}
                                        </div>
                                        <div>/</div>
                                        <div>
                                            {{ item.totaltracks }}
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'discnumber'">
                                    <div v-if="item.discnumber" class="flex">
                                        <div>
                                            {{ item.discnumber }}
                                        </div>
                                        <div>/</div>
                                        <div>
                                            {{ item.totaldiscs }}
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'year'">
                                    <div>
                                        {{ item.year }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'albumartist'" :class="tableSize === 'xs' ? 'truncate max-w-24' : ''">
                                    <div>
                                        {{ item.albumartist }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'album_type'">
                                    <div>
                                        {{ item.album_type }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'genre'">
                                    <div>
                                        {{ item.genre }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'comment'">
                                    <div>
                                        {{ item.comment }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'description'">
                                    <div>
                                        {{ item.description }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'totaltracks'">
                                    <div>
                                        {{ item.totaltracks }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'totaldiscs'">
                                    <div>
                                        {{ item.totaldiscs }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'duration'">
                                    <div>
                                        {{ item.duration }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'size'">
                                    <div>
                                        {{ item.size }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'bit_rate'">
                                    <div>
                                        {{ item.bit_rate }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'sample_rate'">
                                    <div>
                                        {{ item.sample_rate }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'channels'">
                                    <div>
                                        {{ item.channels }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'bit_depth'">
                                    <div>
                                        {{ item.bit_depth }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'language'">
                                    <div>
                                        {{ item.language }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'composer'">
                                    <div>
                                        {{ item.composer }}
                                    </div>
                                </div>
                                <div v-else-if="item2 === 'lyricist'">
                                    <div>
                                        {{ item.lyricist }}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- 加载更多提示 -->
                <div v-if="isLoading" class="flex justify-center py-4">
                    <span class="loading loading-spinner loading-md"></span>
                    <span class="ml-2 text-gray-500">{{ $tt('正在加载...') }}</span>
                </div>
                <div v-else-if="!hasMore && fileId3List.length > 0" class="flex justify-center py-4">
                    <span class="text-gray-400">{{ $tt('已加载全部数据') }}</span>
                </div>
            </div>
        </div>

        <!-- 图片预览Modal -->
        <dialog :class="['modal', {'modal-open': isImagePreviewOpen}]" @click.self="closeImagePreview">
            <div class="modal-box max-w-4xl bg-base-100 p-0">
                <button
                    class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 z-10 bg-base-100/80 hover:bg-base-100"
                    @click="closeImagePreview">
                    ✕
                </button>
                <div class="p-4">
                    <!-- 加载状态 -->
                    <div v-if="isLoadingImage" class="flex justify-center items-center min-h-[400px]">
                        <span class="loading loading-spinner loading-lg"></span>
                        <span class="ml-4 text-gray-500">{{ $tt('正在加载原始封面...') }}</span>
                    </div>
                    <!-- 图片显示 -->
                    <img
                        v-else
                        :src="previewImageUrl"
                        :alt="$tt('封面预览')"
                        class="w-full h-auto rounded-lg"
                        @click.stop
                    />
                </div>
            </div>
            <form method="dialog" class="modal-backdrop" @click="closeImagePreview">
                <button>close</button>
            </form>
        </dialog>
    </div>

</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
import WorkspaceEmptyState from '@/components/home/WorkspaceEmptyState.vue'

export default {
    name: 'multi-meta-data',
    components: {
        SvgIcon,
        WorkspaceEmptyState,
    },
    props: {
        fileId3List: { type: Array, required: true },
        filesNumber: { type: Number, required: false },
        folderNumber: { type: Number, required: false },
        hasMore: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
    },
            data() {
        return {
            tableSize: localStorage.getItem('tableSize') || 'sm',
            showFieldsID3: localStorage.getItem('showFieldsID3') ? JSON.parse(localStorage.getItem('showFieldsID3')) : ['album_img', 'filename', 'title', 'artist', 'album', 'albumartist', 'lyrics', 'lyrics_file', 'year', 'genre'],
            sortedListOrder: ['forward', 'reverse'],
            nowSortedOrder: 'none',
            nowSortedKey: '',
            musicInfo: {},
            isShowEdit2: false,
            drawerMode: 'drawer',
            isShiftPressed: false,
            shiftCheckedList: [],
            // 滑动批量勾选相关状态
            isMouseDown: false,
            startItemIndex: null,
            lastHoveredItemIndex: null,
            startItemChecked: false,
            selectedItems: [],
            selectedItemIds: [],
            // 图片预览相关状态
            isImagePreviewOpen: false,
            previewImageUrl: '',
            isLoadingImage: false,
            // 分页相关
            pageSize: parseInt(localStorage.getItem('limit')) || 32
        }
    },
    computed: {
        checkedData: {
            get() {
                return this.$store.state.common.checkedData
            },
            set(value) {
                this.$store.commit('setCheckedData', value)
                return value
            }
        },
        checkedDataNameList: {
            get() {
                return this.checkedData.map(item => item.name)
            },
        },
        // 计算是否全选
        isAllSelected() {
            return this.fileId3List.length > 0 && this.selectedItemIds.length === this.fileId3List.length
        },
        // 去重的艺术家列表
        uniqueArtistList() {
            const artistSet = new Set()
            this.fileId3List.forEach(item => {
                if (item.artist) {
                    // 处理多个艺术家的情况(用逗号、斜杠等分隔)
                    const artists = item.artist.split(/[,/&、，]/).map(a => a.trim()).filter(a => a)
                    artists.forEach(artist => artistSet.add(artist))
                }
            })
            return Array.from(artistSet).sort().map(artist => ({
                id: artist,
                name: artist
            }))
        },
        // 去重的专辑列表
        uniqueAlbumList() {
            const albumSet = new Set()
            this.fileId3List.forEach(item => {
                if (item.album) {
                    albumSet.add(item.album.trim())
                }
            })
            return Array.from(albumSet).sort().map(album => ({
                id: album,
                name: album
            }))
        },
        // 去重的年份列表
        uniqueYearList() {
            const yearSet = new Set()
            this.fileId3List.forEach(item => {
                if (item.year) {
                    yearSet.add(item.year.toString().trim())
                }
            })
            return Array.from(yearSet).sort((a, b) => b - a).map(year => ({
                id: year,
                name: year
            }))
        },
        // 去重的风格列表
        uniqueGenreList() {
            const genreSet = new Set()
            this.fileId3List.forEach(item => {
                if (item.genre) {
                    genreSet.add(item.genre.trim())
                }
            })
            return Array.from(genreSet).sort().map(genre => ({
                id: genre,
                name: genre
            }))
        },
        // 去重的封面列表
        uniqueArtworkList() {
            const artworkMap = new Map()
            this.fileId3List.forEach(item => {
                if (item.artwork) {
                    // 使用 artwork URL 作为 key,避免重复
                    // 同时记录专辑名称作为标识
                    const key = item.artwork
                    if (!artworkMap.has(key)) {
                        artworkMap.set(key, {
                            id: item.artwork,
                            name: item.album || '未知专辑',
                            url: item.artwork,
                            album: item.album,
                            artist: item.artist
                        })
                    }
                }
            })
            return Array.from(artworkMap.values())
        }
    },
    watch: {
        tableSize(newValue) {
            localStorage.setItem('tableSize', newValue)
        },
        pageSize(newValue) {
            localStorage.setItem('limit', newValue)
        },
        // 监听fileId3List变化，重置选中状态
        fileId3List: {
            handler() {
                this.selectedItems = []
                this.selectedItemIds = []
                // 通知父组件更新去重列表
                this.emitUniqueListsToParent()
            },
            deep: true
        },
        // 监听去重列表变化
        uniqueArtistList: {
            handler() {
                this.emitUniqueListsToParent()
            },
            deep: true
        },
        uniqueAlbumList: {
            handler() {
                this.emitUniqueListsToParent()
            },
            deep: true
        },
        uniqueYearList: {
            handler() {
                this.emitUniqueListsToParent()
            },
            deep: true
        },
        uniqueGenreList: {
            handler() {
                this.emitUniqueListsToParent()
            },
            deep: true
        },
        uniqueArtworkList: {
            handler() {
                this.emitUniqueListsToParent()
            },
            deep: true
        }
    },
    mounted() {
        // 添加全局鼠标事件监听器
        document.addEventListener('mouseup', this.handleGlobalMouseUp)
        // 添加键盘事件监听器
        document.addEventListener('keydown', this.handleKeyDown)
        // 初始化时发送去重列表
        this.emitUniqueListsToParent()
    },
    beforeDestroy() {
        // 移除全局事件监听器以防止内存泄漏
        document.removeEventListener('mouseup', this.handleGlobalMouseUp)
        document.removeEventListener('keydown', this.handleKeyDown)
    },
    methods: {
        // 处理滚动事件,检测是否触底
        handleScroll(event) {
            const container = event.target
            const scrollTop = container.scrollTop
            const scrollHeight = container.scrollHeight
            const clientHeight = container.clientHeight

            // 距离底部小于100px时触发加载
            const threshold = 100
            if (scrollHeight - scrollTop - clientHeight < threshold) {
                if (this.hasMore && !this.isLoading) {
                    this.$emit('load-more')
                }
            }
        },

        // 处理每页显示数量变化
        handlePageSizeChange() {
            this.$emit('page-size-change', this.pageSize)
        },

        // 生成唯一的item ID
        getItemId(item, index) {
            return item.filename ? `${item.filename}-${index}` : `item-${index}`
        },
        
        // 鼠标按下事件处理
        handleMouseDown(item, index, event) {
            if (event.button !== 0) return // 只处理左键点击
            
            event.preventDefault()
            this.isMouseDown = true
            this.startItemIndex = index
            this.lastHoveredItemIndex = index
            this.startItemChecked = this.selectedItemIds.includes(this.getItemId(item, index))
            
            // 切换当前项的选中状态
            this.handleCheck(item, index)
        },
        
        // 鼠标进入事件处理
        handleMouseEnter(item, index, event) {
            if (!this.isMouseDown || this.startItemIndex === null) return
            
            event.preventDefault()
            
            // 防止重复处理同一个节点
            if (this.lastHoveredItemIndex === index) return
            this.lastHoveredItemIndex = index
            
            // 确定选择范围
            const start = Math.min(this.startItemIndex, index)
            const end = Math.max(this.startItemIndex, index)
            
            // 批量更新选中状态
            for (let i = start; i <= end; i++) {
                if (i < this.fileId3List.length) {
                    const currentItem = this.fileId3List[i]
                    const currentItemId = this.getItemId(currentItem, i)
                    
                    // 根据起始项的选中状态来决定是选中还是取消选中
                    if (!this.startItemChecked) {
                        // 如果起始项原本未选中，则选中范围内的所有项
                        if (!this.selectedItemIds.includes(currentItemId)) {
                            this.selectedItemIds.push(currentItemId)
                            this.selectedItems.push({ ...currentItem, index: i })
                        }
                    } else {
                        // 如果起始项原本已选中，则取消选中范围内的所有项
                        const itemIndex = this.selectedItemIds.indexOf(currentItemId)
                        if (itemIndex > -1) {
                            this.selectedItemIds.splice(itemIndex, 1)
                            this.selectedItems.splice(itemIndex, 1)
                        }
                    }
                }
            }
            
            // 同步到父组件的checkedData
            this.syncToCheckedData()
        },
        
        // 鼠标释放事件处理
        handleMouseUp(event) {
            this.handleGlobalMouseUp(event)
        },
        
        // 全局鼠标释放事件处理
        handleGlobalMouseUp(event) {
            if (this.isMouseDown) {
                event.preventDefault()
                this.isMouseDown = false
                this.startItemIndex = null
                this.lastHoveredItemIndex = null
                this.startItemChecked = false
            }
        },
        
        // 处理单个项目的勾选
        handleCheck(item, index) {
            const itemId = this.getItemId(item, index)
            const isSelected = this.selectedItemIds.includes(itemId)
            
            if (isSelected) {
                // 取消选中
                const itemIndex = this.selectedItemIds.indexOf(itemId)
                if (itemIndex > -1) {
                    this.selectedItemIds.splice(itemIndex, 1)
                    this.selectedItems.splice(itemIndex, 1)
                }
            } else {
                // 选中
                this.selectedItemIds.push(itemId)
                this.selectedItems.push({ ...item, index: index })
            }
            
            // 同步到父组件的checkedData
            this.syncToCheckedData()
        },
        
        // 全选/取消全选
        toggleAllSelection() {
            if (this.isAllSelected) {
                // 取消全选
                this.selectedItems = []
                this.selectedItemIds = []
            } else {
                // 全选
                this.selectedItems = this.fileId3List.map((item, index) => ({ ...item, index }))
                this.selectedItemIds = this.fileId3List.map((item, index) => this.getItemId(item, index))
            }
            
            // 同步到父组件的checkedData
            this.syncToCheckedData()
        },
        
        // 同步选中数据到父组件的checkedData
        syncToCheckedData() {
            const newCheckedData = this.selectedItems.map(item => ({
                "checked": true,
                "icon": "icon-script-file",
                "name": item.filename,
                "title": item.filename
            }))
            
            this.checkedData = newCheckedData
        },
        
        handleKeyDown(event) {
            // ESC键关闭图片预览
            if (event.key === 'Escape' && this.isImagePreviewOpen) {
                this.closeImagePreview()
                return
            }

            if (event.shiftKey) {
                this.isShiftPressed = true;
                this.shiftCheckedList = []
            }
        },
        handleKeyUp(event) {
            if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
                this.isShiftPressed = false;
                this.addCheckData2Batch()
            }
        },
        sortBy(key) {
            this.nowSortedKey = key
            this.nowSortedOrder = this.getNextValue()
            if (this.nowSortedOrder === 'reverse') {
                this.fileId3List.sort((a, b) => {
                    if (a[key] > b[key]) return -1
                    if (a[key] < b[key]) return 1
                    return 0
                })
            } else if (this.nowSortedOrder === 'forward') {
                this.fileId3List.sort((a, b) => {
                    if (a[key] > b[key]) return 1
                    if (a[key] < b[key]) return -1
                    return 0
                })
            } else if (this.nowSortedOrder === 'none') {
                console.log()
            }
        },
        getNextValue() {
            let currentIndex = this.sortedListOrder.indexOf(this.nowSortedOrder)
            currentIndex++
            if (currentIndex >= this.sortedListOrder.length) {
                currentIndex = 0
            }
            return this.sortedListOrder[currentIndex]
        },
        handleEdit2(item) {
            this.$parent.handleEdit2(item)
        },
        toggleLock(item) {
            this.$parent.handleEdit2(item)
            this.$parent.toggleLock(this.drawerMode)
        },
        addCheckData(name) {
            if (this.isShiftPressed) {
                if (this.shiftCheckedList.indexOf(name) !== -1) {
                    // 移除
                    const index = this.shiftCheckedList.indexOf(name)
                    this.shiftCheckedList.splice(index, 1)
                } else {
                    this.shiftCheckedList.push(name)
                }
            }
            if (this.checkedDataNameList.indexOf(name) !== -1) {
                // 移除
                const index = this.checkedDataNameList.indexOf(name)
                this.checkedData.splice(index, 1)
                this.$parent.removeFileID3List(index)
            } else {
                this.checkedData.push({
                    "checked": true,
                    "icon": "icon-script-file",
                    "name": name,
                    "title": name
                })
            }
        },
        addCheckData2Batch() {
            if (this.shiftCheckedList.length === 0) {
                return;
            }
            if (this.shiftCheckedList === 1) {
                this.addCheckData(this.shiftCheckedList[0])
                return
            }
            const startName = this.shiftCheckedList[0]
            const endName = this.shiftCheckedList[this.shiftCheckedList.length - 1]
            // 确保 allData 是一个已经排序好的数组，包含所有可能的选择项
            const allData = this.fileId3List.map(item => item.filename);

            // 找到起始和结束名称在 allData 中的索引
            let startIndex = allData.indexOf(startName);
            let endIndex = allData.indexOf(endName);
            console.log(startIndex, endIndex)

            // 检查是否找到了这两个名称
            if (startIndex === -1 || endIndex === -1) {
                console.error('One or both of the provided names are not in the allData list.');
                return;
            }

            // 确保 startIndex <= endIndex
            if (startIndex > endIndex) {
                [startIndex, endIndex] = [endIndex, startIndex];
            }
            // 获取当前已选中的名称列表
            const checkedNames = this.checkedData.map(item => item.name);

            // 将 startName 和 endName 之间的所有项添加到 checkedData 中
            for (let i = startIndex; i <= endIndex; i++) {
                const name = allData[i];

                // 如果名称已经在 checkedData 中，则跳过
                if (checkedNames.includes(name)) {
                    continue;
                }

                // 将新项添加到 checkedData 中
                this.checkedData.push({
                    "checked": true,
                    "icon": "icon-script-file",
                    "name": name,
                    "title": name
                });
            }
        },
        // 打开图片预览
        async openImagePreview(item) {
            if (!item.artwork) return
            // 如果没有parent_path或filename,则直接使用缩略图
            if (!item.parent_path || !item.filename) {
                this.previewImageUrl = item.artwork
                this.isImagePreviewOpen = true
                return
            }

            this.isImagePreviewOpen = true
            this.isLoadingImage = true
            this.previewImageUrl = '' // 先清空

            try {
                const response = await this.$api.Task.getOriginalArtwork({
                    file_full_path: item.path,
                })
                if (response.result) {
                    this.previewImageUrl = response.data.artwork
                } else {
                    // 如果获取失败,使用缩略图
                    this.previewImageUrl = item.artwork
                }
            } catch (error) {
                console.error('获取原始封面失败:', error)
                // 出错时使用缩略图
                this.previewImageUrl = item.artwork
            } finally {
                this.isLoadingImage = false
            }
        },
        // 关闭图片预览
        closeImagePreview() {
            this.isImagePreviewOpen = false
            this.previewImageUrl = ''
            this.isLoadingImage = false
        },
        // 通知父组件更新去重列表
        emitUniqueListsToParent() {
            this.$emit('unique-lists-updated', {
                artists: this.uniqueArtistList,
                albums: this.uniqueAlbumList,
                years: this.uniqueYearList,
                genres: this.uniqueGenreList,
                artworks: this.uniqueArtworkList
            })
        }
    }
}
</script>

<style scoped>
.metadata-empty {
    display: flex;
    min-height: calc(100vh - 120px);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    color: oklch(var(--bc) / 0.62);
    text-align: center;
}

.table {
    position: relative;
}

.table thead {
    position: sticky;
    top: 0;
    background-color: oklch(var(--b1));
    box-shadow: 0 1px 0 oklch(var(--bc) / 0.08);
}

.table thead th {
    background-color: oklch(var(--b1));
    border-bottom: 1px solid oklch(var(--bc) / 0.1);
    position: sticky;
    top: 0;
}

.table thead th::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: oklch(var(--bc) / 0.1);
}

/* 复选框样式 */
.checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
}

.checkbox-wrapper .checkbox {
    pointer-events: none;
}

/* 选中行的高亮样式 */
tr.bg-primary\/10 {
    background-color: oklch(var(--p) / 0.1);
    transition: background-color 0.2s ease;
}

/* 表格行悬停效果 */
tbody tr:hover {
    background-color: oklch(var(--bc) / 0.035);
}

/* 防止用户选择文本 */
.table tbody tr {
    user-select: none;
}

/* 封面图片区域的光标样式 */
.table tbody tr td [class*="grid place-items-center"] {
    cursor: pointer;
}

/* 图片预览Modal样式 */
.modal-box {
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

/* 预览图片的最大高度限制 */
.modal-box img {
    max-height: 85vh;
    object-fit: contain;
}
</style>
